import { ComponentType } from 'react'
import styled from 'styled-components'
import truncate from 'lodash/truncate'

import useFeatureFlag from 'hooks/useFeatureFlag'
import { simplifiedCardTitle } from 'featureFlags'
import Tooltip from 'components/display/Tooltip'
import InstantBookBadge from 'components/display/InstantBookBadge'
import ReviewScore from 'components/reviews/ReviewScore'
import { formatPrice } from 'components/utils/pricing'
import InfoOutline from 'components/icons/InfoOutline'

import LinkOrA from '../LinkOrA'
import { Location, Title } from '../styled'

import CardPricingBreakdown from './CardPricingBreakdown'

const PricingWrapper = styled.div<{ compact?: boolean }>`
  display: flex;
  justify-content: ${({ compact }) =>
    compact ? 'space-between' : 'flex-start'};
  align-items: ${({ compact }) => (compact ? 'center' : 'flex-start')};
`

const Price = styled.div`
  font-size: ${({ theme }) =>
    theme?.['-ds-font-size-xxs']?.['font-size']?.value};
  text-align: right;
  color: ${({ theme }) => theme?.['-ds-neutral-600']?.value};

  strong {
    display: inline-flex;
    align-items: center;
    font-weight: 500;
    color: ${({ theme }) => theme?.['-ds-core-400']?.value};
  }
`

const PriceLarge = styled.div`
  min-width: 85px;

  strong {
    margin-bottom: 4px;
    font-size: ${({ theme }) =>
      theme?.['-ds-font-size-md']?.['font-size']?.value};
  }
`

const PriceCompact = styled.div`
  display: flex;
  align-items: center;

  strong {
    margin-right: 5px;
  }
`

const PriceDetails = styled.span`
  display: flex;
  align-items: center;
  justify-content: flex-end;

  svg {
    top: -2px;
    position: relative;
  }
`

type PricingLineItem = {
  typename?: 'LineItem'
  itemType: string
  quantity: number
  price: number
  amount: number
}

type PricingProps = {
  price: number
  pricing?: {
    typename?: 'Pricing'
    total?: number
    lineItems?: PricingLineItem[]
    customerServiceFee?: number
  }
  pricingSuffix: string
  nicePricingSuffix: string
  instantBookEnabled?: boolean
  compact?: boolean
}

const Pricing = ({
  price,
  pricing,
  pricingSuffix,
  nicePricingSuffix,
  instantBookEnabled,
  compact,
}: PricingProps) => {
  const totalPrice = pricing ? pricing?.total : price

  return (
    <Price as={compact ? PriceCompact : PriceLarge}>
      <strong>
        {instantBookEnabled && <InstantBookBadge compact />}
        {formatPrice(totalPrice, 0)}
      </strong>
      {pricing ? (
        <PriceDetails>
          total
          <Tooltip icon={<InfoOutline />}>
            <CardPricingBreakdown
              pricing={pricing}
              pricingSuffix={pricingSuffix}
            />
          </Tooltip>
        </PriceDetails>
      ) : (
        <div>{nicePricingSuffix}</div>
      )}
    </Price>
  )
}

type ListingDetailsProps = {
  title: string
  capacity: number
  locationName: string
  city: string
  reviewScore?: string
  subcategory: {
    id: string
    name: string
  }
  linkOptions: {
    url: string
    asLink?: boolean
    sameTab?: boolean
  }
  compact?: boolean
  simplifiedTitleFlag?: boolean
} & PricingProps

const ListingDetails = ({
  title,
  capacity,
  locationName,
  city,
  reviewScore,
  subcategory,
  price,
  pricing,
  pricingSuffix,
  nicePricingSuffix,
  instantBookEnabled,
  linkOptions,
  compact,
  simplifiedTitleFlag = false,
}: ListingDetailsProps) => {
  const { url, asLink, sameTab } = linkOptions
  const listingScore = parseFloat(reviewScore)

  const listingTitle = simplifiedTitleFlag
    ? `${subcategory.name} in ${city}`
    : title

  return (
    <>
      <div>
        <LinkOrA
          asLink={asLink}
          to={url}
          sameTab={sameTab}
          ariaLabel={listingTitle}
        >
          <Title compact={compact}>
            {compact ? truncate(listingTitle, { length: 65 }) : listingTitle}
          </Title>
          <Location compact={compact}>
            {!simplifiedTitleFlag && `${compact ? city : locationName} • `}
            Sleeps&nbsp;{capacity}
          </Location>
          {listingScore > 0 && !compact && (
            <ReviewScore score={listingScore} size="small" />
          )}
        </LinkOrA>
      </div>

      <PricingWrapper compact={compact}>
        <Pricing
          price={price}
          pricing={pricing}
          pricingSuffix={pricingSuffix}
          nicePricingSuffix={nicePricingSuffix}
          instantBookEnabled={instantBookEnabled}
          compact={compact}
        />
        {listingScore > 0 && compact && (
          <ReviewScore score={listingScore} size="compact" />
        )}
      </PricingWrapper>
    </>
  )
}

const withFeatureFlags = <P extends Record<string, any>>(
  Component: ComponentType<P>
) => (props: P) => {
  const simplifiedTitleFlag = useFeatureFlag(simplifiedCardTitle)
  const { compact } = props

  return (
    <Component
      {...props}
      simplifiedTitleFlag={simplifiedTitleFlag && compact}
    />
  )
}

export default withFeatureFlags(ListingDetails)
